import React, { useEffect, useState } from "react";
import { FaPlus, FaMinus } from "react-icons/fa";
import { CircularButton } from "./CircularButton";
import { useToast } from "../hooks/useToast";

interface BPMDialProps {
  bpm: number;
  onBPMChange: (newBPM: number) => void;
  currentBeat: number;
  dialFlashingEnabled: boolean;
  stressFirstBeat: boolean;
  stressedBeats: number[];
  controlsLocked?: boolean;
}

// CircularButton is now the shared control button for +/-

export const BPMDial: React.FC<BPMDialProps> = ({
  bpm,
  onBPMChange,
  currentBeat,
  dialFlashingEnabled,
  stressFirstBeat,
  stressedBeats,
  controlsLocked,
}) => {
  const { showInfo, showError } = useToast();
  const [isFlashing, setIsFlashing] = useState(false);
  const [isStressedBeat, setIsStressedBeat] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState(bpm.toString());
  const flashDuration = 50; // Updated to 100ms as per memory

  // Button animation is handled inside CircularButton

  // Update input value when BPM prop changes
  useEffect(() => {
    if (!isEditing) {
      setInputValue(bpm.toString());
    }
  }, [bpm, isEditing]);

  // Flash effect on each beat (only if enabled)
  useEffect(() => {
    if (currentBeat > 0 && dialFlashingEnabled) {
      // Check if this is a stressed beat
      const isStressedBeatOfBar = stressedBeats.includes(currentBeat);
      setIsStressedBeat(isStressedBeatOfBar);

      setIsFlashing(true);
      const timer = setTimeout(() => setIsFlashing(false), flashDuration);
      return () => clearTimeout(timer);
    } else {
      // Reset flash state when metronome stops or flashing is disabled
      setIsFlashing(false);
      setIsStressedBeat(false);
    }
  }, [currentBeat, dialFlashingEnabled, stressedBeats]);

  const handleDecrease = () => {
    if (controlsLocked) return;
    if (bpm > 30) {
      onBPMChange(bpm - 1);
    }
  };

  const handleIncrease = () => {
    if (controlsLocked) return;
    if (bpm < 300) {
      onBPMChange(bpm + 1);
    }
  };

  // Validation function for BPM values
  const validateAndSetBPM = (value: string) => {
    let numValue = parseInt(value);

    // Handle empty or invalid input
    if (isNaN(numValue) || value === "") {
      numValue = bpm; // Keep current BPM if input is invalid
    } else {
      // Clamp to valid range
      const originalValue = numValue;
      if (numValue < 30) numValue = 30;
      if (numValue > 300) numValue = 300;
      if (numValue !== originalValue) {
        showError("Enter a BPM in the range of 30 to 300");
      }
    }

    onBPMChange(numValue);
    setInputValue(numValue.toString());
    setIsEditing(false);
  };

  // Handle clicking on BPM number to edit
  const handleBPMClick = () => {
    if (controlsLocked) {
      showInfo(
        "Editing the BPM and time signature is disabled in Custom Song Structure mode."
      );
      return;
    }
    setIsEditing(true);
    setInputValue(bpm.toString());
  };

  // Handle input focus to select all text
  const handleInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.select();
  };

  // Handle input changes during editing
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    // Only allow numbers and limit to 3 digits
    if (/^\d{0,3}$/.test(value)) {
      setInputValue(value);
    }
  };

  // Handle blur event (when input loses focus)
  const handleInputBlur = () => {
    validateAndSetBPM(inputValue);
  };

  // Handle key press events
  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      validateAndSetBPM(inputValue);
    } else if (e.key === "Escape") {
      setInputValue(bpm.toString());
      setIsEditing(false);
    }
  };

  // Get dial background and shadow based on flash state
  const getDialStyles = () => {
    if (!isFlashing) {
      return {
        background: "var(--overlay-bg)",
        boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
      };
    }

    if (stressFirstBeat && isStressedBeat) {
      return {
        background: "var(--button-hover-bg)",
        boxShadow:
          "0 12px 48px var(--button-hover-bg), 0 8px 32px rgba(0, 0, 0, 0.1)",
      };
    }

    return {
      background: "var(--button-bg)",
      boxShadow: "0 8px 32px var(--button-bg), 0 8px 32px rgba(0, 0, 0, 0.1)",
    };
  };

  const dialStyles = getDialStyles();

  return (
    <div className="bpm-dial-container">
      {/* Minus Button */}
      <div className={`bpm-button-wrapper ${!controlsLocked ? "visible" : ""}`}>
        <CircularButton
          onClick={handleDecrease}
          disabled={bpm <= 30}
          size={48}
          tooltip="Decrease BPM"
          tooltipShortcut="↓"
        >
          {FaMinus({ size: 14 })}
        </CircularButton>
      </div>

      {/* Circular Dial */}
      <div
        className="bpm-dial"
        style={{
          background: dialStyles.background,
          boxShadow: dialStyles.boxShadow,
        }}
      >
        {/* BPM Display */}
        <div className="bpm-dial-text">
          <div className="bpm-dial-label">BPM</div>
          {isEditing ? (
            <input
              type="text"
              inputMode="numeric"
              value={inputValue}
              onChange={handleInputChange}
              onBlur={handleInputBlur}
              onFocus={handleInputFocus}
              onKeyDown={handleInputKeyDown}
              autoFocus
              autoComplete="off"
              style={{
                background: "transparent",
                border: "none",
                color: "var(--text-primary)",
                fontSize: "48px",
                fontWeight: "600",
                textAlign: "center",
                width: "120px",
                outline: "none",
                caretColor: "var(--text-primary)",
              }}
              className="bpm-input"
            />
          ) : (
            <span
              onClick={handleBPMClick}
              style={{
                cursor: "pointer",
                transition: "all 0.2s ease-out",
                display: "inline-block",
                transformOrigin: "center",
              }}
              className="bpm-number"
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor =
                  "var(--button-hover-bg)";
                e.currentTarget.style.borderRadius = "10px";
                e.currentTarget.style.padding = "2px 6px";
                e.currentTarget.style.margin = "-2px -6px";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "transparent";
                e.currentTarget.style.borderRadius = "0";
                e.currentTarget.style.padding = "0";
                e.currentTarget.style.margin = "0";
              }}
            >
              {bpm}
            </span>
          )}
        </div>
      </div>

      {/* Plus Button */}
      <div className={`bpm-button-wrapper ${!controlsLocked ? "visible" : ""}`}>
        <CircularButton
          onClick={handleIncrease}
          disabled={bpm >= 300}
          size={48}
          tooltip="Increase BPM"
          tooltipShortcut="↑"
        >
          {FaPlus({ size: 14 })}
        </CircularButton>
      </div>
    </div>
  );
};
